import React, { useState, useEffect } from 'react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getVoices, textToSpeech } from '../services/voiceService';

export default function VoiceOptions({ darkMode, user }) {
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(() => {
    return localStorage.getItem('fidora_selected_voice') || 'nova';
  });
  const [subscriptionTier, setSubscriptionTier] = useState('free');
  const [previewingVoice, setPreviewingVoice] = useState(null);

  const theme = {
    card: darkMode ? 'rgba(49, 46, 129, 0.4)' : 'rgba(255, 255, 255, 0.85)',
    border: darkMode ? 'rgba(224, 231, 255, 0.15)' : 'rgba(30, 41, 59, 0.1)',
    text: darkMode ? '#ffffff' : '#1e293b',
    subtext: darkMode ? '#e0e7ff' : '#64748b',
    accent: darkMode ? '#818cf8' : '#8b5cf6',
    bg: darkMode ? '#0f172a' : '#f8fafc',
    success: '#10b981',
    error: '#ef4444'
  };

  // Default voices (6 coaches)
  const defaultVoices = [
    { id: 'nova', name: 'Nova', description: 'Caring guide - Deeply empathetic, gentle, patient listener', gender: 'female', avatar: '/jessica.png', style: 'empathetic' },
    { id: 'sage', name: 'Atlas', description: 'Strategic leader - Confident, results-driven, professional excellence', gender: 'male', avatar: '/george2.png', style: 'strategic' },
    { id: 'george', name: 'Sage', description: 'Deep wisdom - Grounded, experienced, thoughtful mentor', gender: 'male', avatar: '/roger.png', style: 'wise' },
    { id: 'marcus', name: 'Phoenix', description: 'Dynamic energy - Bold, transformative, innovative thinking', gender: 'male', avatar: '/bill.png', style: 'dynamic' },
    { id: 'sarah', name: 'Aurora', description: 'Polished grace - Sophisticated, articulate, professional', gender: 'female', avatar: '/rachel.png', style: 'professional' },
    { id: 'maya', name: 'Catalyst', description: 'Change agent - Inspiring, action-oriented, breakthrough focus', gender: 'female', avatar: '/amelia.png', style: 'inspiring' }
  ];

  useEffect(() => {
    loadVoices();
    if (user?.uid) {
      loadSubscriptionTier();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadVoices() {
    try {
      const fetchedVoices = await getVoices();
      // Map fetched voices to default voices to ensure correct names and avatars
      if (fetchedVoices && fetchedVoices.length > 0) {
        const mappedVoices = fetchedVoices.map(fetched => {
          const defaultVoice = defaultVoices.find(dv => dv.id === fetched.id || dv.id === fetched.voice_id);
          if (defaultVoice) {
            // Use the name and avatar from defaultVoices, but keep other properties from fetched
            return {
              ...fetched,
              name: defaultVoice.name,
              avatar: defaultVoice.avatar,
              description: defaultVoice.description,
              gender: defaultVoice.gender,
              style: defaultVoice.style
            };
          }
          return fetched;
        });
        setVoices(mappedVoices);
      } else {
        setVoices(defaultVoices);
      }
    } catch (error) {
      console.error('Error loading voices:', error);
      setVoices(defaultVoices);
    }
  }

  async function loadSubscriptionTier() {
    try {
      const db = getFirestore();
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setSubscriptionTier(data.subscriptionTier || 'free');
      }
    } catch (error) {
      console.error('Error loading subscription tier:', error);
    }
  }

  function handleVoiceSelect(voiceId) {
    const voice = voices.find(v => v.id === voiceId);
    if (!voice) return;

    // Tier-based locking
    let isLocked = false;
    if (subscriptionTier === 'free' || subscriptionTier === 'advisor') {
      isLocked = voiceId !== 'nova';
    } else if (subscriptionTier === 'coach') {
      // Coach tier unlocks Nova and Atlas (which now uses 'sage' voice ID)
      isLocked = !(voiceId === 'nova' || voiceId === 'sage');
    }
    // Mentor tier: all unlocked

    if (isLocked) {
      alert('This voice is locked. Upgrade your subscription to unlock it!');
      return;
    }

    setSelectedVoice(voiceId);
    localStorage.setItem('fidora_selected_voice', voiceId);
    // Dispatch event to update voice name in App.js
    window.dispatchEvent(new CustomEvent('voiceChanged', { detail: voiceId }));
  }

  async function handlePreview(voiceId) {
    if (previewingVoice === voiceId) {
      setPreviewingVoice(null);
      return;
    }

    setPreviewingVoice(voiceId);
    try {
      const previewText = "Hello! I'm your AI career coach, ready to help you succeed.";
      const audioBlob = await textToSpeech(previewText, voiceId);
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audio.play();
      audio.onended = () => {
        setPreviewingVoice(null);
        URL.revokeObjectURL(audioUrl);
      };
    } catch (error) {
      console.error('Error previewing voice:', error);
      setPreviewingVoice(null);
      alert('Failed to preview voice. Please try again.');
    }
  }

  // Filter voices based on tier
  const availableVoices = voices.map(v => {
    let isLocked = false;
    if (subscriptionTier === 'free' || subscriptionTier === 'advisor') {
      isLocked = v.id !== 'nova';
    } else if (subscriptionTier === 'coach') {
      // Coach tier unlocks Nova and Atlas (which now uses 'sage' voice ID)
      isLocked = !(v.id === 'nova' || v.id === 'sage');
    }
    // Mentor tier: all unlocked
    return { ...v, isLocked };
  });

  return (
    <div style={{
      padding: '40px 24px',
      maxWidth: '1400px',
      margin: '0 auto',
      background: theme.bg,
      minHeight: '100vh',
      color: theme.text,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    }}>
      <div style={{ marginBottom: '40px' }}>
        <h1 style={{ 
          fontSize: '2.25rem', 
          fontWeight: 600, 
          marginBottom: '8px', 
          color: theme.text,
          letterSpacing: '-0.02em'
        }}>
          Voice Options
        </h1>
        <p style={{ 
          color: theme.subtext, 
          fontSize: '1rem',
          margin: 0,
          lineHeight: '1.5'
        }}>
          Select your preferred AI coach voice. All coaches provide expert guidance with unique speaking styles.
        </p>
      </div>

      <section style={{
        background: theme.card,
        borderRadius: '20px',
        padding: '40px',
        marginBottom: '32px',
        border: `1px solid ${theme.border}`,
        boxShadow: darkMode 
          ? '0 1px 3px rgba(0, 0, 0, 0.3)' 
          : '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>

        {selectedVoice && (
          <div style={{
            background: darkMode 
              ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)'
              : 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)',
            border: `2px solid ${theme.accent}`,
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '32px',
            display: 'flex',
            alignItems: 'center',
            gap: '20px'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              overflow: 'hidden',
              border: `2px solid ${theme.accent}`,
              flexShrink: 0
            }}>
              <img 
                src={voices.find(v => v.id === selectedVoice)?.avatar || '/jessica.png'} 
                alt={voices.find(v => v.id === selectedVoice)?.name || 'Nova'}
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover',
                  display: 'block'
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                marginBottom: '4px'
              }}>
                <span style={{ 
                  fontSize: '0.75rem', 
                  fontWeight: 600,
                  color: theme.accent,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Currently Selected
                </span>
              </div>
              <div style={{ 
                fontSize: '1.5rem', 
                fontWeight: 600,
                color: theme.text,
                marginBottom: '4px'
              }}>
                {voices.find(v => v.id === selectedVoice)?.name || 'Nova'}
              </div>
              <div style={{ 
                fontSize: '0.875rem', 
                color: theme.subtext,
                lineHeight: '1.5'
              }}>
                {voices.find(v => v.id === selectedVoice)?.description || ''}
              </div>
            </div>
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '24px',
          marginBottom: '24px'
        }}>
          {availableVoices.map((voice) => {
            const isSelected = selectedVoice === voice.id;
            return (
              <div
                key={voice.id}
                onClick={() => !voice.isLocked && handleVoiceSelect(voice.id)}
                style={{
                  background: isSelected 
                    ? (darkMode ? 'rgba(99, 102, 241, 0.15)' : 'rgba(99, 102, 241, 0.06)')
                    : theme.card,
                  border: `2px solid ${isSelected ? theme.accent : theme.border}`,
                  borderRadius: '16px',
                  padding: '24px',
                  cursor: voice.isLocked ? 'not-allowed' : 'pointer',
                  opacity: voice.isLocked ? 0.6 : 1,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  boxShadow: isSelected 
                    ? (darkMode 
                        ? '0 4px 20px rgba(99, 102, 241, 0.25)' 
                        : '0 4px 20px rgba(99, 102, 241, 0.15)')
                    : (darkMode 
                        ? '0 1px 3px rgba(0, 0, 0, 0.2)' 
                        : '0 1px 3px rgba(0, 0, 0, 0.08)'),
                  transform: isSelected ? 'translateY(-2px)' : 'translateY(0)'
                }}
                onMouseEnter={(e) => {
                  if (!voice.isLocked && !isSelected) {
                    e.currentTarget.style.borderColor = theme.accent;
                    e.currentTarget.style.boxShadow = darkMode 
                      ? '0 4px 12px rgba(99, 102, 241, 0.2)' 
                      : '0 4px 12px rgba(99, 102, 241, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor = theme.border;
                    e.currentTarget.style.boxShadow = darkMode 
                      ? '0 1px 3px rgba(0, 0, 0, 0.2)' 
                      : '0 1px 3px rgba(0, 0, 0, 0.08)';
                  }
                }}
              >
                {voice.isLocked && (
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: darkMode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                    Locked
                  </div>
                )}
                {isSelected && !voice.isLocked && (
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: theme.accent,
                    color: 'white',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Selected
                  </div>
                )}
                <div style={{
                  width: '96px',
                  height: '96px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  margin: '0 auto 20px',
                  border: `3px solid ${isSelected ? theme.accent : theme.border}`,
                  background: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                  boxShadow: isSelected 
                    ? (darkMode 
                        ? '0 4px 12px rgba(99, 102, 241, 0.3)' 
                        : '0 4px 12px rgba(99, 102, 241, 0.2)')
                    : 'none',
                  transition: 'all 0.3s ease'
                }}>
                  <img 
                    src={voice.avatar || '/jessica.png'} 
                    alt={voice.name}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover',
                      display: 'block'
                    }}
                    onError={(e) => {
                      e.target.src = '/fidora_fav1.png';
                    }}
                  />
                </div>
                <div style={{
                  textAlign: 'center',
                  color: theme.text,
                  fontWeight: 600,
                  fontSize: '1.25rem',
                  marginBottom: '8px',
                  letterSpacing: '-0.01em'
                }}>
                  {voice.name}
                </div>
                <div style={{
                  textAlign: 'center',
                  color: theme.subtext,
                  fontSize: '0.875rem',
                  marginBottom: '16px',
                  lineHeight: '1.5',
                  minHeight: '40px'
                }}>
                  {voice.description}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!voice.isLocked) {
                      handlePreview(voice.id);
                    }
                  }}
                  disabled={voice.isLocked || previewingVoice === voice.id}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: voice.isLocked 
                      ? theme.border 
                      : (isSelected ? theme.accent : darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'),
                    color: voice.isLocked 
                      ? theme.subtext 
                      : (isSelected ? 'white' : theme.text),
                    border: `1px solid ${voice.isLocked ? theme.border : (isSelected ? theme.accent : theme.border)}`,
                    borderRadius: '8px',
                    cursor: voice.isLocked || previewingVoice === voice.id ? 'not-allowed' : 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    opacity: voice.isLocked ? 0.5 : 1,
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (!voice.isLocked && !previewingVoice) {
                      e.currentTarget.style.background = isSelected ? theme.accent : theme.accent;
                      e.currentTarget.style.color = 'white';
                      e.currentTarget.style.borderColor = theme.accent;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!voice.isLocked) {
                      e.currentTarget.style.background = isSelected ? theme.accent : (darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)');
                      e.currentTarget.style.color = isSelected ? 'white' : theme.text;
                      e.currentTarget.style.borderColor = isSelected ? theme.accent : theme.border;
                    }
                  }}
                >
                  {previewingVoice === voice.id ? (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12 6 12 12 16 14"/>
                      </svg>
                      Playing...
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="5 3 19 12 5 21 5 3"/>
                      </svg>
                      Preview Voice
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

